import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box, Stack, Typography, Button, IconButton,
  Skeleton,
} from "@mui/material";
import { FiPlus, FiRefreshCw, FiArrowLeft, FiX, FiArchive, FiRotateCcw, FiTrash2 } from "react-icons/fi";
import {
  getRecurringTemplates,
  pauseRecurringTemplate,
  resumeRecurringTemplate,
  archiveRecurringTemplate,
  restoreRecurringTemplate,
  deleteRecurringTemplate,
  getTemplateRemovalCheck,
} from "../../services/recurringTemplate.service";
import { showErrorToast as ToastError, showSuccessToast as ToastSuccess } from "../../utils/Toaster/AppToast";
import { showUndoToast } from "../../utils/undoToast";
import ConfirmDialog from "../../sharedComponents/ConfirmDialog/ConfirmDialog";
import RecurringTemplateCard from "./components/RecurringTemplateCard";
import RecurringTemplateForm from "./components/RecurringTemplateForm";

const T = {
  primary: "#0D9488", primaryDark: "#0F766E", primaryLight: "#F0FDFA",
  border: "#E2E8F0", text: "#0F172A", textSub: "#475569", textMuted: "#94A3B8",
  surface: "#FFFFFF", bg: "#F1F5F9",
  shadow: { md: "0 4px 16px rgba(15,23,42,0.08)" },
};

const FILTER_OPTS = [
  { value: "active",   label: "Active"   },
  { value: "paused",   label: "Paused"   },
  { value: "archived", label: "Archived" },
  { value: "all",      label: "All"      },
];

const SkeletonCard = () => (
  <Box sx={{ bgcolor: T.surface, border: `1px solid ${T.border}`, borderRadius: "14px", p: 2.5 }}>
    <Skeleton variant="text" width="60%" height={24} />
    <Skeleton variant="text" width="40%" height={18} sx={{ mt: 0.5 }} />
    <Stack direction="row" gap={1} sx={{ mt: 1 }}>
      <Skeleton variant="rounded" width={60} height={20} sx={{ borderRadius: "5px" }} />
      <Skeleton variant="rounded" width={80} height={20} sx={{ borderRadius: "5px" }} />
    </Stack>
  </Box>
);

// view: "list" | "create" | "edit"
const RecurringTemplateMain = ({ onClose, initialTemplate = null, initialView = null, prefillData = null }) => {
  const startView = initialTemplate ? "edit" : (initialView || "list");
  const [view,            setView]            = useState(startView);
  const [templates,       setTemplates]       = useState([]);
  const [loading,         setLoading]         = useState(startView === "list");
  const [statusFilter,    setStatusFilter]    = useState("active");
  const [editingTemplate, setEditingTemplate] = useState(initialTemplate);
  // Archive/Delete confirmation. `state` is the server's removal check:
  // { canRemove, total, started, unstarted, breakdown }.
  const [removal, setRemoval] = useState({
    open: false, loading: false, template: null, action: null, state: null,
  });
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const isAdmin = localStorage.getItem("isOverAllAdmin") === "true";
      const params = { status: statusFilter || undefined };
      if (!isAdmin) {
        const userId = localStorage.getItem("userId");
        if (userId) params.assignedBy = userId;
      }
      const res = await getRecurringTemplates(params);
      if (res?.error === false) setTemplates(res.data || []);
    } catch {
      ToastError("Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // ── Optimistic helpers ────────────────────────────────────────────────────
  const removeTemplate = (id) => setTemplates((prev) => prev.filter((t) => t._id !== id));
  const addTemplate    = (t)  => setTemplates((prev) => [t, ...prev]);

  // ── Actions ───────────────────────────────────────────────────────────────
  const handlePause = async (id) => {
    const res = await pauseRecurringTemplate(id);
    if (res?.error === false) fetchTemplates();
    else ToastError("Failed to pause");
  };

  const handleResume = async (id) => {
    const res = await resumeRecurringTemplate(id);
    if (res?.error === false) fetchTemplates();
    else ToastError("Failed to resume");
  };

  // Archive and Delete are gated on the template's spawned tasks, so they ask
  // the server first and open a dialog. The optimistic remove + Undo pattern
  // cannot express a refusal — its commit result is never read — so it is kept
  // only for Restore, which is non-destructive.
  const openRemovalDialog = useCallback(async (template, action) => {
    setRemoval({ open: true, loading: true, template, action, state: null });
    try {
      const res = await getTemplateRemovalCheck(template._id);
      if (res?.error === false) {
        setRemoval((r) => ({ ...r, loading: false, state: res.data }));
      } else {
        setRemoval({ open: false, loading: false, template: null, action: null, state: null });
        ToastError(res?.message || "Could not check the template's tasks");
      }
    } catch (err) {
      setRemoval({ open: false, loading: false, template: null, action: null, state: null });
      ToastError(err?.data?.message || "Could not check the template's tasks");
    }
  }, []);

  const closeRemovalDialog = () =>
    setRemoval({ open: false, loading: false, template: null, action: null, state: null });

  const confirmRemoval = useCallback(async () => {
    const { template, action, state } = removal;
    if (!template || !state?.canRemove) return closeRemovalDialog();

    setRemoval((r) => ({ ...r, loading: true }));
    const call = action === "delete" ? deleteRecurringTemplate : archiveRecurringTemplate;
    try {
      const res = await call(template._id);
      if (res?.error === false) {
        ToastSuccess(res?.message || `Template ${action}d`);
        closeRemovalDialog();
        removeTemplate(template._id);
        fetchTemplates();
        return;
      }
      ToastError(res?.message || `${action} failed`);
      setRemoval((r) => ({ ...r, loading: false }));
    } catch (err) {
      // A 409 refusal rejects with the body nested at err.data — err.message is
      // the interceptor's generic "Response error occurred".
      const body = err?.data;
      if (err?.status === 409 && body?.data) {
        // Someone started a task between the check and the confirm.
        setRemoval((r) => ({ ...r, loading: false, state: body.data }));
        return;
      }
      ToastError(body?.message || `${action} failed`);
      setRemoval((r) => ({ ...r, loading: false }));
    }
  }, [removal]);

  const handleArchive = useCallback((template) => openRemovalDialog(template, "archive"), [openRemovalDialog]);

  const handleRestore = useCallback((template) => {
    removeTemplate(template._id);
    showUndoToast(
      `"${template.title}" restored`,
      () => restoreRecurringTemplate(template._id).catch(() => ToastError("Restore failed")),
      () => addTemplate(template),
    );
  }, []);

  const handleDelete = useCallback((template) => openRemovalDialog(template, "delete"), [openRemovalDialog]);

  // Dialog copy. Three shapes: still checking, refused (work already started),
  // and clear-to-remove — the last one names what goes with the template.
  const blocked = !!removal.state && !removal.state.canRemove;
  const removalCopy = (() => {
    const verb = removal.action === "delete" ? "Delete" : "Archive";
    const name = removal.template?.title ? `"${removal.template.title}"` : "this template";

    if (!removal.state) {
      return { title: `${verb} ${name}?`, description: "Checking its tasks…", confirmLabel: verb };
    }
    if (blocked) {
      const detail = removal.state.breakdown.map((b) => `${b.count} ${b.label}`).join(" · ");
      return {
        title: `Can't ${verb.toLowerCase()} ${name}`,
        description:
          `${removal.state.started} of its ${removal.state.total} tasks have already been worked on — ${detail}. ` +
          `Finish or remove those tasks first.`,
        confirmLabel: "Got it",
      };
    }
    return {
      title: `${verb} ${name}?`,
      description: removal.state.unstarted
        ? `It will stop generating tasks, and its ${removal.state.unstarted} unstarted task(s) will be removed with it.`
        : "It will stop generating tasks. No tasks have been spawned yet.",
      confirmLabel: verb,
    };
  })();

  const isArchived = statusFilter === "archived";

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* ── Drawer header ─────────────────────────────────────────────────── */}
      <Box sx={{ px: 2.5, py: 2, bgcolor: T.surface, borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" gap={1.5}>
            {(view === "create" || view === "edit") && (
              <IconButton
                size="small"
                onClick={() => { setView("list"); setEditingTemplate(null); }}
                sx={{ color: T.textSub, "&:hover": { bgcolor: T.primaryLight, color: T.primary } }}
              >
                <FiArrowLeft size={16} />
              </IconButton>
            )}
            <Box>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography sx={{ fontSize: "16px", fontWeight: 800, color: T.text, lineHeight: 1.1 }}>
                  {view === "create" ? "New Recurring Template"
                    : view === "edit" ? "Edit Recurring Template"
                    : "Recurring Tasks"}
                </Typography>
                {view === "edit" && editingTemplate?.templateNo && (
                  <Typography sx={{ fontSize: "11px", fontWeight: 700, color: T.primary, bgcolor: "#F0FDFA", px: "6px", py: "2px", borderRadius: "5px", border: "1px solid #99F6E4", fontFamily: "monospace", flexShrink: 0 }}>
                    #{editingTemplate.templateNo}
                  </Typography>
                )}
              </Stack>
              <Typography sx={{ fontSize: "11px", color: T.textMuted, mt: "2px" }}>
                {view === "create"
                  ? "Define the blueprint — tasks auto-generate on schedule"
                  : view === "edit"
                  ? "Update the blueprint settings"
                  : "Blueprints that auto-spawn tasks on your schedule"}
              </Typography>
            </Box>
          </Stack>

          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: T.textMuted, "&:hover": { color: T.text, bgcolor: "#F1F5F9" } }}
          >
            <FiX size={18} />
          </IconButton>
        </Stack>
      </Box>

      {/* ── Scrollable body ───────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <AnimatePresence mode="wait" initial={false}>

          {/* LIST VIEW */}
          {view === "list" && (
            <motion.div
              key="list"
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <Box sx={{ p: 2 }}>

                {/* ── Toolbar ───────────────────────────────────────────── */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>

                  {/* Filter pills */}
                  <Box
                    sx={{
                      display: "inline-flex", bgcolor: T.bg,
                      borderRadius: "10px", p: "3px",
                      border: `1px solid ${T.border}`,
                    }}
                  >
                    {FILTER_OPTS.map(({ value, label }) => {
                      const active = statusFilter === value;
                      const isArchivedPill = value === "archived";
                      return (
                        <Box
                          key={value}
                          component="button"
                          onClick={() => setStatusFilter(value)}
                          sx={{
                            px: "11px", py: "6px",
                            border: "none", borderRadius: "7px",
                            cursor: "pointer", fontFamily: "inherit",
                            fontSize: "12px", fontWeight: active ? 700 : 500,
                            bgcolor: active
                              ? isArchivedPill ? "#D97706" : T.surface
                              : "transparent",
                            color: active
                              ? isArchivedPill ? "#fff" : T.text
                              : T.textMuted,
                            boxShadow: active && !isArchivedPill ? "0 1px 4px rgba(15,23,42,0.09)" : "none",
                            transition: "all 0.15s",
                          }}
                        >
                          {label}
                        </Box>
                      );
                    })}
                  </Box>

                  {/* New Template — hidden when viewing archived */}
                  {!isArchived && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<FiPlus size={13} />}
                      onClick={() => setView("create")}
                      sx={{ bgcolor: T.primary, fontWeight: 700, fontSize: "13px", "&:hover": { bgcolor: T.primaryDark } }}
                    >
                      New Template
                    </Button>
                  )}
                </Stack>

                {/* ── Archived section label ─────────────────────────── */}
                {isArchived && (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: "7px", bgcolor: "#FFFBEB", border: "1px solid #FDE68A", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <FiArchive size={13} color="#D97706" />
                    </Box>
                    <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#D97706" }}>
                      Archived Templates
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: T.textMuted }}>
                      · {templates.length} template{templates.length !== 1 ? "s" : ""}
                    </Typography>
                  </Stack>
                )}

                {/* ── Template list ──────────────────────────────────── */}
                {loading ? (
                  <Stack spacing={1.5}>
                    {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
                  </Stack>
                ) : templates.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 10 }}>
                    <Box sx={{
                      width: 64, height: 64, borderRadius: "18px",
                      bgcolor: T.surface, border: `1px solid ${T.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      mx: "auto", mb: 2,
                    }}>
                      {isArchived
                        ? <FiArchive size={26} color="#CBD5E1" />
                        : <FiRefreshCw size={26} color="#CBD5E1" />}
                    </Box>
                    <Typography sx={{ fontSize: "15px", fontWeight: 700, color: T.textSub }}>
                      {isArchived ? "No archived templates" : "No recurring templates"}
                    </Typography>
                    <Typography sx={{ fontSize: "13px", color: T.textMuted, mt: 0.5, mb: isArchived ? 0 : 2.5 }}>
                      {isArchived
                        ? "Archived templates will appear here"
                        : "Create one and tasks will auto-generate on your schedule"}
                    </Typography>
                    {!isArchived && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<FiPlus size={13} />}
                        onClick={() => setView("create")}
                        sx={{ bgcolor: T.primary, fontWeight: 700, "&:hover": { bgcolor: T.primaryDark } }}
                      >
                        Create First Template
                      </Button>
                    )}
                  </Box>
                ) : (
                  <Stack spacing={1.5}>
                    <AnimatePresence>
                      {templates.map((t, i) => (
                        <motion.div
                          key={t._id}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, x: -16, height: 0, marginBottom: 0 }}
                          transition={{ duration: 0.18, delay: i * 0.04 }}
                        >
                          <RecurringTemplateCard
                            template={t}
                            onPause={() => handlePause(t._id)}
                            onResume={() => handleResume(t._id)}
                            onArchive={() => handleArchive(t)}
                            onRestore={() => handleRestore(t)}
                            onDelete={() => handleDelete(t)}
                            onView={() => { setEditingTemplate(t); setView("edit"); }}
                          />
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </Stack>
                )}
              </Box>
            </motion.div>
          )}

          {/* CREATE VIEW */}
          {view === "create" && (
            <motion.div
              key="create"
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 24 }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <Box sx={{ p: 2.5 }}>
                <RecurringTemplateForm
                  prefillData={prefillData}
                  onSuccess={() => { setView("list"); fetchTemplates(); }}
                  onCancel={() => setView("list")}
                />
              </Box>
            </motion.div>
          )}

          {/* EDIT VIEW */}
          {view === "edit" && editingTemplate && (
            <motion.div
              key="edit"
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 24 }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <Box sx={{ p: 2.5 }}>
                <RecurringTemplateForm
                  template={editingTemplate}
                  onSuccess={() => { setView("list"); setEditingTemplate(null); fetchTemplates(); }}
                  onCancel={() => { setView("list"); setEditingTemplate(null); }}
                />
              </Box>
            </motion.div>
          )}

        </AnimatePresence>
      </Box>

      <ConfirmDialog
        open={removal.open}
        onClose={closeRemovalDialog}
        onConfirm={blocked ? closeRemovalDialog : confirmRemoval}
        loading={removal.loading}
        variant={blocked ? "warning" : removal.action === "delete" ? "danger" : "warning"}
        title={removalCopy.title}
        description={removalCopy.description}
        confirmLabel={removalCopy.confirmLabel}
        hideCancel={blocked}
      />
    </Box>
  );
};

export default RecurringTemplateMain;
